import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';

const CategoryCarousel = ({ title, products, onAddToCart }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, products]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 360;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="py-8 relative group/carousel">
      {/* Header */}
      <div className="container mx-auto px-6 mb-7 flex justify-between items-end">
        <div>
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h3 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight">
            {title}
          </h3>
          <p className="text-sm text-gray-400 font-medium mt-1.5">
            {products.length} {products.length === 1 ? 'product' : 'products'}
          </p>
        </div>

        {/* Desktop navigation */}
        <div className="hidden md:flex gap-2.5">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
            className={`p-3.5 rounded-full border transition-all duration-300 active:scale-90 ${
              canScrollLeft
                ? 'bg-white border-gray-200 text-gray-800 hover:bg-[#1d1d1f] hover:text-white hover:border-[#1d1d1f] shadow-sm'
                : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            aria-label="Scroll right"
            className={`p-3.5 rounded-full border transition-all duration-300 active:scale-90 ${
              canScrollRight
                ? 'bg-white border-gray-200 text-gray-800 hover:bg-[#1d1d1f] hover:text-white hover:border-[#1d1d1f] shadow-sm'
                : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative">
        {/* Edge fades (desktop) */}
        <div
          className={`hidden md:block absolute left-0 top-0 bottom-12 w-16 bg-gradient-to-r from-[#f5f5f7] to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            canScrollLeft ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`hidden md:block absolute right-0 top-0 bottom-12 w-16 bg-gradient-to-l from-[#f5f5f7] to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            canScrollRight ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-5 md:gap-6 px-6 pb-12 no-scrollbar snap-x snap-mandatory scroll-pl-6"
          style={{ scrollBehavior: 'smooth' }}
        >
          {products.map((product, idx) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={onAddToCart}
              index={idx}
            />
          ))}
          <div className="w-6 flex-shrink-0" />
        </div>
      </div>
    </div>
  );
};

export default CategoryCarousel;
